import {
  useCallback,
} from 'react';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import {
  AlertTypeDto,
  ExecuteAlertActionRequestDTO,
  PatientAlertDTO,
} from 'dtos';
import {
  AlertStatuses,
} from 'dtos/alertStatuses';
import {
  getApiAlertsGetAlertTypes,
  postApiAlertsExecuteAction,
  postApiAlertsGetPatientAlerts,
} from 'endpoints';
import useSignalREffect from 'views/EMR/hooks/signalR';
import {
  patientAlertsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  ExecuteAlertActionResponseDTO,
} from 'dtos/executeAlertActionResponseDTO';

export function getPatientAlertsQueryKey(
  patientId: number,
  encounterId?: number,
): (string | number | null)[] {
  return ['patientAlerts', patientId, encounterId ?? null];
}

export function useGetAlertTypes(): UseQueryResult<AlertTypeDto[], Error> {
  return useQuery<AlertTypeDto[], Error>({
    queryKey: ['alertTypes'],
    queryFn: () => getApiAlertsGetAlertTypes(),
  });
}

export function useGetPatientAlerts(
  patientId: number,
  encounterId?: number,
): UseQueryResult<PatientAlertDTO[], Error> {
  return useQuery<PatientAlertDTO[], Error>({
    queryKey: getPatientAlertsQueryKey(patientId, encounterId),
    queryFn: () => postApiAlertsGetPatientAlerts({
      patientId,
      status: AlertStatuses.Active,
      encounterId: encounterId ?? null,
    }),
    enabled: patientId > 0,
  });
}

export function useExecuteAlertAction(): UseMutationResult<
ExecuteAlertActionResponseDTO, Error, ExecuteAlertActionRequestDTO> {
  return useMutation<ExecuteAlertActionResponseDTO, Error, ExecuteAlertActionRequestDTO>({
    mutationFn: (executeAlertActionRequestDTO) => postApiAlertsExecuteAction(
      executeAlertActionRequestDTO,
    ),
  });
}

export function usePatientAlertsSignalRInvalidation(): void {
  const onPatientAlertUpdated = useCallback((
    patientId: number,
  ) => {
    if (isNil(patientId) || patientId <= 0) {
      return;
    }

    patientAlertsEventChannel.emit(
      'onPatientAlertsChanged',
      patientId,
      undefined,
    );
  }, []);

  useSignalREffect('OnPatientAlertUpdated', onPatientAlertUpdated, [onPatientAlertUpdated]);
}
